import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, CheckCircle, XCircle, Clock, AlertCircle, RefreshCw, 
  ChevronDown, ChevronRight, User, Building2, FileText, FolderOpen,
  ArrowRight, GitBranch, Zap, Mail, Globe, Play, ArrowDownRight
} from 'lucide-react';
import api from '../utils/api';


const WorkflowExecutionLogs = ({ workflow, onClose }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    fetchExecutions();
  }, [workflow?.id, filterStatus]);

  const fetchExecutions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const params = new URLSearchParams();
      if (workflow?.id) {
        params.append('workflow_id', workflow.id);
      }
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      params.append('limit', '50');
      
      const response = await api.get(`/workflow-executions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExecutions(response.data.executions || []);
    } catch (err) {
      console.error('Error fetching executions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutionDetails = async (executionId) => {
    if (selectedExecution === executionId && executionDetails) {
      return;
    }
    
    try {
      setDetailsLoading(true);
      setSelectedExecution(executionId);
      const token = localStorage.getItem('access_token');
      const response = await api.get(`/workflow-executions/${executionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setExecutionDetails(response.data);
      // Auto-expand all nodes for better visibility
      const initialExpanded = {};
      (response.data.logs || []).forEach(log => {
        initialExpanded[log.id] = true;
      });
      setExpandedNodes(initialExpanded);
    } catch (err) {
      console.error('Error fetching execution details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const getStatusIcon = (status, size = 5) => {
    const sizeClass = size === 4 ? 'w-4 h-4' : 'w-5 h-5';
    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircle className={`${sizeClass} text-green-600`} />;
      case 'failed':
        return <XCircle className={`${sizeClass} text-red-600`} />;
      case 'running':
        return <Clock className={`${sizeClass} text-blue-600 animate-spin`} />;
      case 'needs_review':
      case 'partial':
        return <AlertCircle className={`${sizeClass} text-yellow-600`} />;
      case 'skipped':
        return <ArrowRight className={`${sizeClass} text-gray-400`} />;
      default:
        return <Clock className={`${sizeClass} text-gray-400`} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'queued':
      case 'waiting':
      case 'running':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'needs_review':
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'skipped':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTriggerType = (type) => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatActionType = (action) => {
    if (!action) return '';
    return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getNodeIcon = (nodeType, nodeAction) => {
    if (nodeType === 'trigger') return <Zap className="w-4 h-4 text-green-600" />;
    if (nodeType === 'condition') return <GitBranch className="w-4 h-4 text-yellow-600" />;
    if (nodeAction === 'email' || nodeAction === 'send_portal_invite') return <Mail className="w-4 h-4 text-blue-600" />;
    if (nodeAction === 'webhook') return <Globe className="w-4 h-4 text-purple-600" />;
    return <Play className="w-4 h-4 text-blue-600" />;
  };

  const toggleNodeExpand = (nodeId, e) => {
    e.stopPropagation();
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  // Build a readable summary from trigger data
  const buildTriggerSummary = (triggerData) => {
    if (!triggerData) return null;
    
    const items = [];
    
    if (triggerData.contact?.name) {
      items.push({ icon: <User className="w-4 h-4" />, label: 'Contact', value: triggerData.contact.name });
    }
    if (triggerData.contact?.email) {
      items.push({ icon: <Mail className="w-4 h-4" />, label: 'Email', value: triggerData.contact.email });
    }
    if (triggerData.client?.name) {
      items.push({ icon: <Building2 className="w-4 h-4" />, label: 'Client', value: triggerData.client.name });
    }
    if (triggerData.task?.name) {
      items.push({ icon: <FileText className="w-4 h-4" />, label: 'Task', value: triggerData.task.name });
    }
    if (triggerData.project?.name) {
      items.push({ icon: <FolderOpen className="w-4 h-4" />, label: 'Project', value: triggerData.project.name });
    }
    
    return items;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Execution Logs</h2>
              {workflow && (
                <p className="text-sm text-gray-600">{workflow.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="partial">Partial</option>
              <option value="running">Running</option>
              <option value="queued">Queued</option>
              <option value="waiting">Waiting</option>
              <option value="needs_review">Needs review</option>
            </select>
            <button
              onClick={fetchExecutions}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-73px)]">
        {/* Executions List - Narrower */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : executions.length === 0 ? (
            <div className="text-center py-12 px-4">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No executions found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {executions.map((execution) => (
                <div
                  key={execution.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchExecutionDetails(execution.id);
                  }}
                  className={`p-3 cursor-pointer transition-all ${
                    selectedExecution === execution.id 
                      ? 'bg-purple-50 border-l-4 border-purple-600' 
                      : 'hover:bg-gray-50 border-l-4 border-transparent'
                  }`}
                >
                  {/* Status row */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(execution.status, 4)}
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(execution.status)}`}>
                        {execution.status}
                      </span>
                    </div>
                    {execution.duration_ms && (
                      <span className="text-xs text-gray-400">{execution.duration_ms}ms</span>
                    )}
                  </div>
                  
                  {/* Main identifier - Summary or fallback */}
                  <div className="font-medium text-gray-900 text-sm truncate">
                    {execution.summary || execution.summary_details?.contact || execution.summary_details?.client || formatTriggerType(execution.trigger_type)}
                  </div>
                  
                  {/* Secondary info */}
                  {execution.summary && (
                    <div className="text-xs text-gray-500 truncate">
                      {formatTriggerType(execution.trigger_type)}
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(execution.started_at).toLocaleString()}
                  </div>
                  
                  {execution.is_test_run && (
                    <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      Test
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Execution Details Panel - Takes remaining space */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {!selectedExecution ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Clock className="w-16 h-16 mb-4" />
              <p className="text-lg">Select an execution to view details</p>
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : executionDetails ? (
            <div className="space-y-6">
              {/* Execution Header */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{executionDetails.execution.workflow_name}</h3>
                    <p className="text-sm text-gray-500">{formatTriggerType(executionDetails.execution.trigger_type)}</p>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusColor(executionDetails.execution.status)}`}>
                    {getStatusIcon(executionDetails.execution.status, 4)}
                    <span className="font-medium capitalize text-sm">{executionDetails.execution.status.replace(/_/g, ' ')}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Started</p>
                    <p className="font-medium">{new Date(executionDetails.execution.started_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Completed</p>
                    <p className="font-medium">
                      {executionDetails.execution.completed_at 
                        ? new Date(executionDetails.execution.completed_at).toLocaleString()
                        : 'In Progress'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Duration</p>
                    <p className="font-medium">{executionDetails.execution.duration_ms || 0}ms</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Nodes</p>
                    <p className="font-medium">
                      <span className="text-green-600">{executionDetails.execution.success_node_count || 0} ✓</span>
                      {executionDetails.execution.failed_node_count > 0 && (
                        <span className="text-red-600 ml-2">{executionDetails.execution.failed_node_count} ✗</span>
                      )}
                    </p>
                  </div>
                </div>

                {executionDetails.execution.error_message && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">Error: {executionDetails.execution.error_message}</p>
                  </div>
                )}
              </div>

              {/* Trigger Context - What triggered this */}
              {executionDetails.execution.trigger_data && (
                <div className="bg-white rounded-lg border border-gray-200 p-5">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-green-600" />
                    Trigger Context
                  </h4>
                  
                  {(() => {
                    const triggerData = typeof executionDetails.execution.trigger_data === 'string' 
                      ? JSON.parse(executionDetails.execution.trigger_data) 
                      : executionDetails.execution.trigger_data;
                    const items = buildTriggerSummary(triggerData);
                    
                    if (items && items.length > 0) {
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {items.map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                              <div className="text-gray-500 mt-0.5">{item.icon}</div>
                              <div>
                                <p className="text-xs text-gray-500">{item.label}</p>
                                <p className="text-sm font-medium text-gray-900">{item.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(triggerData, null, 2)}
                      </pre>
                    );
                  })()}
                </div>
              )}

              {/* Execution Flow */}
              <div className="bg-white rounded-lg border border-gray-200 p-5">
                <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <GitBranch className="w-4 h-4 text-purple-600" />
                  Execution Flow
                </h4>
                
                {executionDetails.logs && executionDetails.logs.length > 0 ? (
                  <div className="space-y-3">
                    {executionDetails.logs.map((log, index) => {
                      // Get the friendly label for this node
                      const nodeLabel = executionDetails.node_labels?.[log.node_id] || formatActionType(log.node_action) || log.node_type;
                      
                      return (
                        <div key={log.id} className="relative">
                          {/* Connection line */}
                          {index < executionDetails.logs.length - 1 && (
                            <div className="absolute left-5 top-12 w-0.5 h-8 bg-gray-200" />
                          )}
                          
                          <div className={`border rounded-lg overflow-hidden ${
                            log.status === 'failed' ? 'border-red-300 bg-red-50/30' : 'border-gray-200'
                          }`}>
                            {/* Node Header */}
                            <div
                              onClick={(e) => toggleNodeExpand(log.id, e)}
                              className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                            >
                              {/* Step indicator */}
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                log.status === 'success' || log.status === 'completed' ? 'bg-green-100' :
                                log.status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                              }`}>
                                {getNodeIcon(log.node_type, log.node_action)}
                              </div>
                              
                              {/* Node info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{nodeLabel}</span>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(log.status)}`}>
                                    {log.status}
                                  </span>
                                </div>
                                {/* Human-readable summary */}
                                {log.summary && (
                                  <div className={`text-sm mt-1 ${log.status === 'failed' ? 'text-red-600' : 'text-gray-700'}`}>
                                    {log.summary}
                                  </div>
                                )}
                                <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                                  <span className="capitalize">{log.node_type}</span>
                                  {log.node_action && (
                                    <>
                                      <span>•</span>
                                      <span>{formatActionType(log.node_action)}</span>
                                    </>
                                  )}
                                  {log.duration_ms !== null && (
                                    <>
                                      <span>•</span>
                                      <span>{log.duration_ms}ms</span>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Expand indicator */}
                              {expandedNodes[log.id] ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              )}
                            </div>

                            {/* Expanded Details */}
                            {expandedNodes[log.id] && (
                              <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-3">
                                {/* Error */}
                                {log.error_message && (
                                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                    <p className="text-xs font-semibold text-red-700 mb-1">Error</p>
                                    <p className="text-sm text-red-800">{log.error_message}</p>
                                  </div>
                                )}
                                
                                {/* Output - Human readable format */}
                                {log.output_data && Object.keys(log.output_data).length > 0 && (
                                  <div>
                                    <p className="text-xs font-semibold text-gray-600 mb-2">Result Details</p>
                                    <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
                                      {/* Show message if present */}
                                      {log.output_data.message && (
                                        <p className="text-sm text-gray-800">{log.output_data.message}</p>
                                      )}
                                      {log.output_data.verification_sent && (
                                        <p className="text-xs text-green-600">✓ Verification email sent</p>
                                      )}
                                      
                                      {/* Task info */}
                                      {log.output_data.task && (
                                        <div className="text-sm">
                                          <span className="font-medium text-gray-700">Task:</span>{' '}
                                          <span className="text-gray-600">{log.output_data.task.name}</span>
                                          {log.output_data.task.status && (
                                            <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded text-xs">{log.output_data.task.status}</span>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Status change info */}
                                      {(log.output_data.old_status || log.output_data.old_stage_name) && (
                                        <div className="text-sm text-gray-600">
                                          <span className="font-medium text-gray-700">Changed:</span>{' '}
                                          {log.output_data.old_status || log.output_data.old_stage_name} → {log.output_data.new_status || log.output_data.new_stage_name}
                                        </div>
                                      )}
                                      
                                      {/* Branch info for if/else */}
                                      {(log.output_data.branch_id || log.output_data.matched_branch) && (
                                        <div className="text-sm text-gray-600">
                                          <span className="font-medium text-gray-700">Branch taken:</span>{' '}
                                          {log.output_data.matched_branch || log.output_data.branch_id}
                                        </div>
                                      )}
                                      
                                      {/* Recipient info for emails/notifications */}
                                      {log.output_data.recipient && (
                                        <div className="text-sm text-gray-600">
                                          <span className="font-medium text-gray-700">Recipient:</span>{' '}
                                          {log.output_data.recipient}
                                        </div>
                                      )}
                                      
                                      {/* Assignees info */}
                                      {log.output_data.assignees && log.output_data.assignees.length > 0 && (
                                        <div className="text-sm text-gray-600">
                                          <span className="font-medium text-gray-700">Assignees:</span>{' '}
                                          {log.output_data.assignee_names?.join(', ') || `${log.output_data.assignees.length} assigned`}
                                        </div>
                                      )}
                                      
                                      {/* Show raw data toggle for advanced users */}
                                      <details className="text-xs mt-2">
                                        <summary className="text-gray-500 cursor-pointer hover:text-gray-700">
                                          View raw data
                                        </summary>
                                        <pre className="mt-2 bg-gray-50 p-2 rounded text-xs overflow-x-auto max-h-32">
                                          {JSON.stringify(log.output_data, null, 2)}
                                        </pre>
                                      </details>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Input - Collapsible for advanced users */}
                                {log.input_data && Object.keys(log.input_data).length > 0 && (
                                  <details className="text-xs">
                                    <summary className="font-semibold text-gray-600 cursor-pointer hover:text-gray-800">
                                      View Input Data
                                    </summary>
                                    <pre className="mt-2 bg-white p-3 rounded-lg border border-gray-200 overflow-x-auto max-h-40">
                                      {JSON.stringify(log.input_data, null, 2)}
                                    </pre>
                                  </details>
                                )}
                                
                                <div className="text-xs text-gray-400">
                                  Executed: {new Date(log.executed_at).toLocaleString()}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No execution steps recorded</p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default WorkflowExecutionLogs;
